'use client'

import { ReactNode, useEffect, useId, useRef, useSyncExternalStore } from 'react'
import { createPortal } from 'react-dom'
import useBlockScroll from '@/app/hooks/useBlockScroll'
import useOnClickOutside from '@/app/hooks/useOnClickOutside'

interface LoanModalShellProps {
    badge?: { label: string; tone: 'success' | 'error' }
    title: ReactNode
    onClose?: () => void
    children: ReactNode
    maxWidthClassName?: string
}

// Le portail n'existe qu'après hydratation : un store constant l'exprime sans setState dans un effet
const subscribeToNothing = () => () => {}

const BADGE_TONES = {
    success: 'bg-green-100 text-green-800',
    error: 'bg-red-100 text-red-700',
}

const BADGE_DOTS = {
    success: 'bg-green-600',
    error: 'bg-red-500',
}

const FOCUSABLE_SELECTOR =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

const focusablesIn = (node: HTMLElement) =>
    Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter((el) => el.offsetParent !== null)

export default function LoanModalShell({
    badge,
    title,
    onClose,
    children,
    maxWidthClassName = 'max-w-[800px]',
}: LoanModalShellProps) {
    const modalRef = useRef<HTMLDivElement>(null)
    const titleId = useId()
    const mounted = useSyncExternalStore(
        subscribeToNothing,
        () => true,
        () => false
    )

    useBlockScroll()
    useOnClickOutside(modalRef, () => onClose?.(), 'mousedown', '.react-datepicker')

    const onCloseRef = useRef(onClose)
    useEffect(() => {
        onCloseRef.current = onClose
    }, [onClose])

    // Le focus doit revenir au déclencheur : sinon il repart en haut de page à la fermeture
    useEffect(() => {
        const trigger = document.activeElement as HTMLElement | null
        return () => trigger?.focus?.()
    }, [])

    // Focus initial isolé de `onClose` : inline chez les appelants, il ramènerait au 1er champ à chaque render
    useEffect(() => {
        const node = modalRef.current
        if (!node) return

        const first = focusablesIn(node)[0]
        if (first) first.focus()
        else node.focus()
    }, [mounted])

    useEffect(() => {
        const node = modalRef.current
        if (!node) return

        const handleKeyDown = (event: KeyboardEvent) => {
            // An open datepicker closes first, and being in a portal it is looked up on the document
            if (event.key === 'Escape') {
                if (!document.querySelector('.react-datepicker')) onCloseRef.current?.()
                return
            }
            if (event.key !== 'Tab') return

            const items = focusablesIn(node)
            if (!items.length) return

            const start = items[0]
            const end = items[items.length - 1]
            const active = document.activeElement
            // `aria-modal` promet un focus captif : un clic sur le fond l'a sorti de la modale
            if (!node.contains(active)) {
                event.preventDefault()
                ;(event.shiftKey ? end : start).focus()
            } else if (event.shiftKey && active === start) {
                event.preventDefault()
                end.focus()
            } else if (!event.shiftKey && active === end) {
                event.preventDefault()
                start.focus()
            }
        }

        document.addEventListener('keydown', handleKeyDown)
        return () => document.removeEventListener('keydown', handleKeyDown)
    }, [mounted])

    if (!mounted) return null

    return createPortal(
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
            <div
                ref={modalRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                tabIndex={-1}
                className={`relative z-10 mx-4 max-h-[90vh] w-full overflow-y-auto rounded-2xl bg-white p-8 shadow-2xl ${maxWidthClassName}`}
            >
                {onClose && (
                    <button
                        onClick={onClose}
                        aria-label="Fermer"
                        className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full text-pale/50 hover:bg-neutral hover:text-pale"
                    >
                        <i className="fa-regular fa-times" />
                    </button>
                )}

                {badge && (
                    <span
                        className={`mb-4 inline-flex items-center gap-2 rounded-full px-3 py-1 text-[13px] font-semibold ${BADGE_TONES[badge.tone]}`}
                    >
                        <span className={`h-2 w-2 rounded-full ${BADGE_DOTS[badge.tone]}`} />
                        {badge.label}
                    </span>
                )}

                <h3
                    id={titleId}
                    style={{ fontFamily: 'var(--font-recoleta)' }}
                    className="mb-4 block px-0 text-[22px] leading-[1.2] font-semibold text-pale"
                >
                    {title}
                </h3>

                {children}
            </div>
        </div>,
        document.body
    )
}
